import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { getAuthToken, refreshAccessToken } from "../utils/auth";
import useReconnectingWebSocket from "../components/chat/useReconnectingWebSocket";

const WebSocketContext = createContext();

// Refresca el access token si caduca en menos de este margen (segundos).
const WS_TOKEN_REFRESH_SKEW_S = 60;

// Lee el `exp` (epoch s) del JWT sin verificar firma. Devuelve true si el
// token va a caducar pronto (o ya caducó). Pura, tolerante a tokens raros.
function tokenExpiringSoon(token, skewSeconds = WS_TOKEN_REFRESH_SKEW_S) {
  try {
    const payloadB64 = token.split(".")[1];
    const json = JSON.parse(
      atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (!json || !json.exp) return false;
    return json.exp - Date.now() / 1000 < skewSeconds;
  } catch (_) {
    return false;
  }
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const token = getAuthToken();
  const WS_ENDPOINT = process.env.REACT_APP_WS_ENDPOINT;

  // Resuelve la URL del WS con un token SIEMPRE válido. Se invoca en cada
  // (re)conexión: si el access token está a punto de caducar, lo refresca
  // antes de conectar (igual que hace el interceptor de axios en REST).
  const resolveWsUrl = useCallback(async () => {
    let current = getAuthToken();
    if (!current || !WS_ENDPOINT) return null;
    if (tokenExpiringSoon(current)) {
      try {
        await refreshAccessToken({
          backendUrl: process.env.REACT_APP_BACKEND_URL,
          failedToken: current,
        });
      } catch (_) {
        // Refresh falló (red / refresh expirado): seguimos con el token
        // actual; si el server lo rechaza, el hook reintenta con backoff.
      }
      current = getAuthToken() || current;
    }
    if (!current) return null;
    return `${WS_ENDPOINT}?token=${encodeURIComponent(current)}`;
  }, [WS_ENDPOINT]);

  const { socket, safeSend } = useReconnectingWebSocket(
    resolveWsUrl,
    !!token
  );

  const [globalMessageHandlers, setGlobalMessageHandlers] = useState([]);
  const handlersRef = useRef([]);

  // Actualizar la referencia cuando cambien los handlers
  useEffect(() => {
    handlersRef.current = globalMessageHandlers;
  }, [globalMessageHandlers]);

  // Registrar handlers globales para mensajes WebSocket
  const addMessageHandler = useCallback((handler) => {
    setGlobalMessageHandlers((prev) => {
      if (prev.includes(handler)) {
        return prev;
      }
      return [...prev, handler];
    });
  }, []);

  const removeMessageHandler = useCallback((handler) => {
    setGlobalMessageHandlers((prev) => prev.filter((h) => h !== handler));
  }, []);

  // Manejar mensajes WebSocket globalmente
  useEffect(() => {
    if (!socket) {
      return;
    }

    const handleMessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (!data || !data.action) {
          // Ignore control/error frames with no action (e.g., API GW default route errors)
          return;
        }
        console.log("📨 Global WebSocket message:", data.action);

        // Ejecutar todos los handlers registrados usando la referencia
        handlersRef.current.forEach((handler) => {
          try {
            handler(data);
          } catch (error) {
            console.error("❌ Error in global message handler:", error);
          }
        });

        // Broadcast para UI que no registró handler (p. ej., Plantillas)
        try {
          if (data.action === "template_status_update") {
            window.dispatchEvent(
              new CustomEvent("conextcrm:template_status_update", {
                detail: data,
              })
            );
          }
        } catch (_) {}
      } catch (error) {
        console.error("❌ Error parsing global WebSocket message:", error);
      }
    };

    socket.addEventListener("message", handleMessage);

    return () => {
      socket.removeEventListener("message", handleMessage);
    };
  }, [socket]); // Removido globalMessageHandlers de las dependencias

  const value = useMemo(
    () => ({
      socket,
      safeSend,
      addMessageHandler,
      removeMessageHandler,
      isConnected: socket?.readyState === WebSocket.OPEN,
      hasToken: !!token,
    }),
    [addMessageHandler, removeMessageHandler, safeSend, socket, token]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
